import { useState } from 'react';
import { Share2, Copy, MessageCircle, Send, MessageSquare, Check, X } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { useToast } from '@/components/ui/Toast';

interface ShareAppProps {
    variant?: 'card' | 'button' | 'icon';
    className?: string;
}

export default function ShareApp({ variant = 'card', className = '' }: ShareAppProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const shareUrl = window.location.origin;
    const shareText = t('share.message') as string + shareUrl;

    const handleCopy = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast(t('share.copied'), 'success');
        setTimeout(() => setCopied(false), 2000);
    };

    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'TipsNorge',
                    text: t('share.message'),
                    url: shareUrl,
                });
            } catch (err) {
                console.error('Error sharing:', err);
            }
        } else {
            setIsOpen(true);
        }
    };

    const shareLinks = [
        {
            name: t('share.whatsapp'),
            icon: <MessageCircle className="w-5 h-5" />,
            color: 'bg-[#25D366]',
            url: `https://wa.me/?text=${encodeURIComponent(shareText)}`
        },
        {
            name: t('share.messenger'),
            icon: <Send className="w-5 h-5" />,
            color: 'bg-[#0084FF]',
            url: `fb-messenger://share/?link=${encodeURIComponent(shareUrl)}&app_id=123456789`
        },
        {
            name: t('share.sms'),
            icon: <MessageSquare className="w-5 h-5" />,
            color: 'bg-slate-700',
            url: `sms:?body=${encodeURIComponent(shareText)}`
        }
    ];

    // Web Messenger fallback if deep link fails or on desktop
    const messengerWebUrl = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareUrl)}&app_id=123456789&redirect_uri=${encodeURIComponent(shareUrl)}`;

    return (
        <>
            {variant === 'card' && (
                <button
                    onClick={handleNativeShare}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleNativeShare(); }}
                    className={`w-full bg-blue-50 text-blue-600 p-4 rounded-2xl border border-blue-100 flex items-center justify-between group hover:bg-blue-100 transition-all active:scale-[0.98] ${className}`}
                    aria-label={t('share.title')}
                >
                    <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded-xl shadow-sm group-hover:scale-110 transition-transform">
                            <Share2 className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                            <p className="font-bold text-sm text-slate-900">{t('share.title')}</p>
                            <p className="text-xs text-slate-500">{t('share.subtitle')}</p>
                        </div>
                    </div>
                    <div className="bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-lg shadow-blue-500/20">
                        {t('share.btn')}
                    </div>
                </button>
            )}

            {variant === 'button' && (
                <button
                    onClick={handleNativeShare}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleNativeShare(); }}
                    className={`bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-all shadow-md active:scale-95 flex items-center justify-center ${className}`}
                    title={t('common.share')}
                    aria-label={t('common.share')}
                >
                    <Share2 className="w-5 h-5" />
                </button>
            )}

            {variant === 'icon' && (
                <button
                    onClick={handleNativeShare}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleNativeShare(); }}
                    className={`p-2 text-slate-500 hover:text-blue-600 transition-colors ${className}`}
                    title={t('common.share')}
                    aria-label={t('common.share')}
                >
                    <Share2 className="w-5 h-5" />
                </button>
            )}

            {/* Manual Share Modal (for Desktop/Browsers without native share) */}
            {isOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl p-6 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-black text-slate-900">{t('share.title')}</h3>
                            <button
                                onClick={() => setIsOpen(false)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsOpen(false); }}
                                className="p-2 hover:bg-slate-100 rounded-full text-slate-400"
                                aria-label="Close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-8">
                            {shareLinks.map((link) => (
                                <a
                                    key={link.name}
                                    href={link.name === t('share.messenger') && !/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? messengerWebUrl : link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-col items-center gap-2 group"
                                >
                                    <div className={`${link.color} text-white p-4 rounded-2xl shadow-lg transition-transform group-hover:scale-110 group-active:scale-95`}>
                                        {link.icon}
                                    </div>
                                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{link.name}</span>
                                </a>
                            ))}
                        </div>

                        <div className="space-y-2">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{t('share.copy')}</p>
                            <div className="flex gap-2">
                                <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-500 truncate">
                                    {shareUrl}
                                </div>
                                <button
                                    onClick={handleCopy}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCopy(); }}
                                    className="bg-slate-900 text-white p-3 rounded-xl hover:bg-slate-800 transition-colors"
                                    aria-label={t('share.copy')}
                                >
                                    {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
